import { z } from 'zod';
export const GiftPhotoVariant = z.enum(['Individual', 'Couple', 'Family']);
const shortText = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !/[\p{Cc}\p{Cf}]/u.test(value), 'Use a single line without control characters');
export const GiftCardDraft = z
    .object({
    recipientName: shortText(80),
    experience: shortText(140),
    personalMessage: z
        .string()
        .trim()
        .max(200)
        .refine((value) => !/[\p{Cc}\p{Cf}]/u.test(value.replace(/\r?\n/g, '')), 'Use plain text without control characters')
        .optional(),
    validFrom: z.iso.date(),
    validUntil: z.iso.date(),
    photoVariant: GiftPhotoVariant,
    photoAssetId: z.uuid().nullable(),
})
    .strict()
    .refine((value) => value.validUntil > value.validFrom, {
    path: ['validUntil'],
    message: 'Valid until must be after the start date',
});
export const GiftCardRecord = z
    .object({
    fields: GiftCardDraft,
    code: z.string().regex(/^DP-[A-F0-9]{12}$/),
    revision: z.number().int().positive(),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const GiftCardResponse = z
    .object({ giftCard: GiftCardRecord.nullable(), pdfReady: z.boolean() })
    .strict();
export const SaveGiftCardRequest = z
    .object({
    fields: GiftCardDraft,
    expectedRevision: z.number().int().nonnegative(),
    expectedShootRevision: z.number().int().nonnegative(),
})
    .strict();
/** A calendar year, clamped at the last day of February for leap-day gifts. */
export function giftCardExpiry(value) {
    const date = z.iso.date().parse(value);
    const [year, month, day] = date.split('-').map(Number);
    const last = new Date(Date.UTC(year + 1, month, 0)).getUTCDate();
    return `${year + 1}-${String(month).padStart(2, '0')}-${String(Math.min(day, last)).padStart(2, '0')}`;
}
export function giftCardToday(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Lisbon',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const part = (type) => parts.find((value) => value.type === type).value;
    return `${part('year')}-${part('month')}-${part('day')}`;
}
/** Stateless, authenticated rendering. Optional context preserves retained custom photos. */
export const GiftCardPreviewRequest = z
    .object({ fields: GiftCardDraft, shootId: z.uuid().optional() })
    .strict();
export const GiftCardPreviewResponse = z.object({ svg: z.string().min(1).max(8_388_608) }).strict();
